import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useIssuerEntitiesQuery } from '@/modules/issuer-dashboard/queries/entities.ts';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import { SearchFieldWithOptions, SelectOption } from 'ui';
import { EntityStatus } from 'api';

type entityOptionValue = {
  entity_id: string;
  id: number;
  status?: EntityStatus;
};

const renderSearchFieldOptionLabel: (option: SelectOption<entityOptionValue>) => ReactNode = ({
  label,
  value,
}) => (
  <div className="w-full flex justify-between text-sm">
    <div className="flex items-center gap-2">
      <span>{label}</span>
      {value.status === EntityStatus.EntityAwaitingApproval && (
        <span className="text-[12px] leading-[20px] text-white bg-blue-700 rounded-[1000px] px-2 py-[2px] capitalize whitespace-nowrap">
          Pending
        </span>
      )}
    </div>
    <span className="text-neutral-500">{value.entity_id}</span>
  </div>
);

const HolderSearch = () => {
  const [params] = useSearchParams();
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const currentRegisterId = +(params.get(REGISTER_ID_SEARCH_PARAM_KEY) || '0');

  const approvedEntitiesQuery = useIssuerEntitiesQuery({
    search,
    page_size: 15,
    status: EntityStatus.EntityApproved,
    register_ids: [currentRegisterId].toString(),
  });

  const awaitingApprovalEntitiesQuery = useIssuerEntitiesQuery({
    search,
    page_size: 15,
    status: EntityStatus.EntityAwaitingApproval,
    register_ids: [currentRegisterId].toString(),
  });

  const entitiesOptions = useMemo(() => {
    const optionsMap = new Map<number, SelectOption<entityOptionValue>>();

    (approvedEntitiesQuery.data?.pages || []).forEach(({ data }) => {
      data.forEach(({ entity_name, id, entity_id, status }) => {
        optionsMap.set(id, {
          label: entity_name,
          value: { id, entity_id, status },
        });
      });
    });

    (awaitingApprovalEntitiesQuery.data?.pages || []).forEach(({ data }) => {
      data.forEach(({ entity_name, id, entity_id, status }) => {
        optionsMap.set(id, {
          label: entity_name,
          value: { id, entity_id, status },
        });
      });
    });

    return Array.from(optionsMap.values());
  }, [approvedEntitiesQuery.data?.pages, awaitingApprovalEntitiesQuery.data?.pages]);

  const isEntitiesFetching =
    approvedEntitiesQuery.isFetching || awaitingApprovalEntitiesQuery.isFetching;

  const hasNextEntitiesPage =
    approvedEntitiesQuery.hasNextPage || awaitingApprovalEntitiesQuery.hasNextPage;

  const fetchNextEntitiesPage = useCallback(
    () =>
      Promise.all([
        approvedEntitiesQuery.hasNextPage
          ? approvedEntitiesQuery.fetchNextPage()
          : Promise.resolve(),
        awaitingApprovalEntitiesQuery.hasNextPage
          ? awaitingApprovalEntitiesQuery.fetchNextPage()
          : Promise.resolve(),
      ]),
    [
      approvedEntitiesQuery.fetchNextPage,
      approvedEntitiesQuery.hasNextPage,
      awaitingApprovalEntitiesQuery.fetchNextPage,
      awaitingApprovalEntitiesQuery.hasNextPage,
    ],
  );

  const handleSelectEntity = useCallback(
    (value: entityOptionValue) => {
      navigate(
        {
          pathname: issuerDashboardPaths.dynamic.holder(value.id.toString()),
          search: params.toString(),
        },
        {
          state: { from: pathname + '?' + params.toString() },
        },
      );
    },
    [navigate, params, pathname],
  );

  return (
    <SearchFieldWithOptions<entityOptionValue>
      options={entitiesOptions}
      onSelect={handleSelectEntity}
      hasMore={hasNextEntitiesPage}
      isLoading={isEntitiesFetching}
      fetchNextPage={fetchNextEntitiesPage}
      onSearch={setSearch}
      renderLabel={renderSearchFieldOptionLabel}
      placeholder="HIN/SRN or Holder Name"
    />
  );
};

export default HolderSearch;
